import {addRoleSuffix, getSimplexUnitName} from 'helpers/simplex-helper';
import Const from 'constants';
import RuleBuilder from 'services/simplex/rule-builder';
import {createSimplexCompetitionKey} from 'components/organisms/competition-stacking/helpers';

function normalizeOpponentKey(opponentKey) {
    return opponentKey.replace(/[\s\-]/gu, '');
}

function isBothOpponentsTakePart(opponents) {
    return 2 === opponents.length;
}

function mapOpponents(opponents) {
    const mappedOpponents = {};
    opponents.map((opponent) => {
        const opponentKey = normalizeOpponentKey(opponent.team);
        mappedOpponents[opponent.getId()] = opponent;
    });
    return mappedOpponents;
}

function createConstraintForOpponents(params) {
    const {opponents, modes} = params;
    const mappedOpponents = mapOpponents(opponents);
    const constraints = {};

    Object.keys(mappedOpponents).map((opponentKey) => {
        const opponent = mappedOpponents[opponentKey];
        const builder = RuleBuilder.fromExactly(0);

        if (isEmpty(modes)) {
            builder.addVar(getSimplexUnitName(opponent.getId()), 1);
        } else {
            modes.map((mode) => {
                const lpPlayerId = addRoleSuffix({
                    prefix: opponent.getId(),
                    role: mode,
                });
                builder.addVar(getSimplexUnitName(lpPlayerId), 1);
            });
        }

        const simplexKey = Const.LP_TEAM_GROUP_PREFIX + opponentKey;

        builder.addVar(simplexKey, -1);
        constraints[opponentKey] = builder.createRule();
    });

    return constraints;
}

function createConstraintForCompetitions(params) {
    const {competitionKeys} = params;
    const constraints = {};

    competitionKeys.map((opponentKeys) => {
        const builderAtLeast = RuleBuilder.fromAtLeast(0);
        const builderAtMost = RuleBuilder.fromAtMost(1);

        opponentKeys.map((opponentKey) => {
            builderAtLeast.addVar(Const.LP_TEAM_GROUP_PREFIX + opponentKey, 1);
            builderAtMost.addVar(Const.LP_TEAM_GROUP_PREFIX + opponentKey, 1);
        });

        const simplexKey = createSimplexCompetitionKey(opponentKeys);

        builderAtLeast.addVar(simplexKey, -1 * opponentKeys.length);
        builderAtMost.addVar(simplexKey, -1 * opponentKeys.length);

        constraints[simplexKey] = [builderAtLeast.createRule(), builderAtMost.createRule()];
    });

    return constraints;
}

function createConstraintForNotStackableOpponents(params) {
    const {competitions, modes} = params;
    const constraints = [];
    const simplexKeys = [];

    Object.values(competitions).map((opponents) => {
        const opponentsConstraints = createConstraintForOpponents({
            opponents,
            modes,
        });

        const opponentKeys = Object.keys(opponentsConstraints);

        if (!isBothOpponentsTakePart(opponentKeys)) {
            return;
        }

        const builder = RuleBuilder.fromAtMost(1);

        opponentKeys.map((opponentKey) => {
            const simplexKey = Const.LP_TEAM_GROUP_PREFIX + opponentKey;
            builder.addVar(simplexKey, 1);
            constraints.push(opponentsConstraints[opponentKey]);
            simplexKeys.push(simplexKey);
        });

        constraints.push(builder.createRule());
    });

    return {
        simplexKeys,
        constraints,
    };
}

function createConstraintForStackableOpponents(params) {
    const {competitions, modes, ruleBuilder} = params;
    const constraints = [];
    const simplexKeys = [];

    const competitionKeys = [];
    Object.values(competitions).map((opponents) => {
        const opponentsConstraint = createConstraintForOpponents({
            opponents,
            modes,
        });

        const opponentKeys = Object.keys(opponentsConstraint);

        if (!isBothOpponentsTakePart(opponentKeys)) {
            return;
        }

        opponentKeys.map((opponentKey) => {
            const simplexKey = Const.LP_TEAM_GROUP_PREFIX + opponentKey;
            constraints.push(opponentsConstraint[opponentKey]);
            simplexKeys.push(simplexKey);
        });

        competitionKeys.push(opponentKeys);
    });

    const competitionConstraint = createConstraintForCompetitions({competitionKeys});

    Object.keys(competitionConstraint).map((simplexKey) => {
        ruleBuilder.addVar(simplexKey, 1);
        competitionConstraint[simplexKey].map((constraint) => {
            constraints.push(constraint);
        });
        simplexKeys.push(simplexKey);
    });

    if (ruleBuilder.isRuleCreatable()) {
        constraints.push(ruleBuilder.createRule());
    }

    return {
        simplexKeys,
        constraints,
    };
}

class ConstraintsBuilder {
    constructor(dependencies) {
        this._dependencies = {...dependencies};
    }

    createConstraintForNotStackableOpponents(params) {
        const {modes} = this._dependencies;
        return createConstraintForNotStackableOpponents({
            modes,
            ...params,
        });
    }

    createConstraintForStackableOpponents(params) {
        const {modes} = this._dependencies;
        const ruleBuilder = RuleBuilder.fromExactly(1);
        return createConstraintForStackableOpponents({
            modes,
            ruleBuilder,
            ...params,
        });
    }

    createConstraintForStackedOpponents(params) {
        const {modes} = this._dependencies;
        const ruleBuilder = RuleBuilder.fromAtMost(1);
        return createConstraintForStackableOpponents({
            modes,
            ruleBuilder,
            ...params,
        });
    }
}

export {ConstraintsBuilder};
